import Component from '@glimmer/component';
import { service } from '@ember/service';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Button from 'tio-reimagined/components/button';
import FeatureCard from 'tio-reimagined/components/feature-card';
import SectionHeader from 'tio-reimagined/components/section-header';
import PageHero from 'tio-reimagined/components/page-hero';
import type { DataSourceInfo } from 'tio-employee/services/reimagined-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type PartnerService from 'tio-employee/services/partner';
import {
  PhEnvelope,
  PhPhone,
  PhChatCircleDots,
  PhQuestion,
  PhBookOpen,
  PhArrowRight,
  PhCalendar,
  PhRobot,
} from 'ember-phosphor-icons';

interface ContactModel {
  dataSourceInfo: DataSourceInfo;
}

interface ContactSignature {
  Args: {
    model: ContactModel;
  };
}

interface FaqItem {
  question: string;
  answer: string;
}

const COMMON_FAQS: FaqItem[] = [
  {
    question: 'How do I add my student loans?',
    answer:
      'Go to the Onboarding page or Documents section and upload your loan statements or NSLDS file from StudentAid.gov. You can upload a recent loan servicer statement or download your NSLDS file for automatic loan detection.',
  },
  {
    question: 'When will I receive my employer contribution?',
    answer:
      'Employer contributions are typically processed monthly on the same schedule as payroll. Check the Finance section for your next expected payment date.',
  },
  {
    question: 'How do I apply for tuition assistance?',
    answer:
      'Navigate to Benefits > Tuition Assistance and click "Start New Application." You\'ll need your program details, course information, and any required approvals.',
  },
  {
    question: 'What is the Section 127 benefit?',
    answer:
      'Section 127 of the IRS code allows your employer to contribute up to $5,250 per year tax-free toward your education expenses, including student loan repayment.',
  },
  {
    question: 'How do I reset my password?',
    answer:
      'Click "Forgot Password" on the login page, enter your email, and follow the reset link sent to your inbox. You can also change your password from the Profile page.',
  },
];

export default class ContactPage extends Component<ContactSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @service declare partner: PartnerService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get supportEmail() {
    const support = this.partner.supportModule;
    if (!support || support?.tioEmail?.isEnabled) {
      return 'support@tuition.io';
    }
    return support?.partnerEmail ?? 'support@tuition.io';
  }

  get faqs() {
    return COMMON_FAQS;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero @theme="sky" @decoration="circles" @rounded="2xl" @centered={{true}} class="mb-0">
        <:icon>
          <div class="flex h-16 w-16 items-center justify-center rounded-xl bg-white/20">
            <PhChatCircleDots class="h-9 w-9 text-white" />
          </div>
        </:icon>
        <:default>
          <div class="text-center">
            <Heading @level={{2}} class="text-white text-2xl font-bold mb-2">
              How Can We Help?
            </Heading>
            <Text class="text-sky-100 max-w-lg mx-auto">
              Our support team is here to help you make the most of your financial benefits. Reach
              out through any of the channels below.
            </Text>
          </div>
        </:default>
      </PageHero>

      {{! Contact Methods }}
      <SectionHeader
        @icon={{PhEnvelope}}
        @title="Get In Touch"
        @subtitle="Choose your preferred way to reach us"
        @iconClass="text-blue-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <FeatureCard
          @icon={{PhEnvelope}}
          @title="Email Us"
          @description="Send us a detailed message and we'll respond within 24 hours."
          @color="blue"
          @centered={{true}}
        >
          <:action>
            <a
              href="mailto:{{this.supportEmail}}"
              class="text-blue-600 hover:text-blue-800 dark:text-blue-400 font-medium text-sm"
            >
              {{this.supportEmail}}
            </a>
          </:action>
        </FeatureCard>
        <FeatureCard
          @icon={{PhPhone}}
          @title="Call Us"
          @description="Mon-Fri, 8am-6pm CT"
          @color="green"
          @centered={{true}}
        >
          <:action>
            <a
              href="tel:18556855100"
              class="text-green-600 hover:text-green-800 dark:text-green-400 font-medium text-sm"
            >
              1-855-685-5100
            </a>
          </:action>
        </FeatureCard>
        <FeatureCard
          @icon={{PhChatCircleDots}}
          @title="Live Chat"
          @description="Chat with a real person for instant help."
          @color="purple"
          @centered={{true}}
        >
          <:action>
            <Button @intent="primary" @appearance="outlined">
              Start Chat
            </Button>
          </:action>
        </FeatureCard>
      </div>

      {{! AI Advisor Promo }}
      <div
        class="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-2xl p-6 flex items-center gap-6"
      >
        <div
          class="flex h-14 w-14 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/30 shrink-0"
        >
          <PhRobot class="h-7 w-7 text-violet-600" />
        </div>
        <div class="flex-1">
          <Heading @level={{4}} class="font-semibold mb-1">Try Our AI Advisor</Heading>
          <Text class="text-gray-600 text-sm">
            Get instant answers about your benefits, loans, and financial strategies 24/7.
          </Text>
        </div>
        <Button @intent="primary">
          Ask AI
          <PhArrowRight class="ml-2 h-4 w-4" />
        </Button>
      </div>

      {{! FAQs }}
      <SectionHeader
        @icon={{PhQuestion}}
        @title="Common Questions"
        @subtitle="Quick answers to frequent questions"
        @iconClass="text-sky-500"
      />
      <div class="space-y-3">
        {{#each this.faqs as |faq|}}
          <div class="bg-white rounded-xl border border-gray-200 p-5">
            <div class="flex items-start gap-3">
              <div
                class="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30 shrink-0 mt-0.5"
              >
                <PhQuestion class="h-4 w-4 text-sky-600" />
              </div>
              <div>
                <Heading @level={{4}} class="font-semibold text-gray-900 mb-2">
                  {{faq.question}}
                </Heading>
                <Text class="text-gray-600 text-sm leading-relaxed">
                  {{faq.answer}}
                </Text>
              </div>
            </div>
          </div>
        {{/each}}
      </div>

      {{! Help Resources }}
      <SectionHeader
        @icon={{PhBookOpen}}
        @title="Help Resources"
        @subtitle="Self-service tools and guides"
        @iconClass="text-amber-500"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FeatureCard
          @icon={{PhBookOpen}}
          @title="Glossary"
          @description="Look up financial terms and student loan jargon."
          @color="amber"
        />
        <FeatureCard
          @icon={{PhCalendar}}
          @title="Coaching"
          @description="Schedule a session with a certified student loan coach."
          @color="teal"
        />
        <FeatureCard
          @icon={{PhQuestion}}
          @title="Knowledge Base"
          @description="Browse articles on student loans, benefits, and financial planning."
          @color="indigo"
        />
      </div>
    </VStack>
  </template>
}
